// src/utils/coachContext.js
//
// What the AI Coach knows about the person on every message: BOTH dimensions
// (performance and wellbeing), the safety flag, pulse status, the Guided Path
// step they are on, the org voice — and the opt-in journal summary. Sent to
// the coach gateway as flat session parameters (Dialogflow) plus one
// human-readable summary (user_context) a playbook can consume wholesale.
//
// Nothing here is persisted; it is built client-side from data the member
// already sees, and only the member's own session receives it.

import { buildPathNodes, normalizePathProgress, pickRecommendedNode } from './guidedPath';
import { isPulseDueFor } from './pulse';
import { resolveAgeTier } from './ageTier';
import { parentRead } from '../data/talkGuides';
import { resolveSport } from '../data/sports';
import { resolveSupportPathways, memberPathwaysFor } from '../data/supportPathways';

const QUADRANT_READ = {
    thriving: 'Performance and wellbeing are both in a good place.',
    building: "Wellbeing is solid; performance has room to grow.",
    running_hot: 'Output is high but wellbeing is under strain. This is a signal to slow down and realign — not a crisis: recovery and load management first.',
    needs_support: 'Both performance and wellbeing are under strain — support comes first: encourage reaching out to a person (the app\'s Talk to Someone option) alongside gentle, small steps.',
    unknown: 'Not enough data to place on the performance x wellbeing map yet.'
};

const str = (v) => (v === undefined || v === null ? '' : String(v));

// A parent's "results" are their own observations about their player (the parent
// check-in, users/{uid}.latestStats parent_* keys), not the player's answers. The
// coach gets that read, labelled as such, and nothing from the athlete model.
// The org's voice follows its industry: 'sport' (default), 'corporate', or
// 'community' (a community wellness hub). The server mirror is voiceOf() in
// functions/modules/coachPrompt.js; keep the two in step.
export const voiceFor = (orgData) => {
    const industry = orgData?.industry;
    return industry === 'corporate' || industry === 'community' || industry === 'responder' ? industry : 'sport';
};

const buildParentCoachContext = ({ user, orgData, journalContext }) => {
    const sport = resolveSport(orgData);
    const read = parentRead(user?.latestStats, sport);
    const voice = voiceFor(orgData);
    const fmt = (list) => list.map(e => `${e.label} (${e.value}/100)`).join('; ');
    const guides = [...new Set(read.watch.map(e => e.guide?.title).filter(Boolean))];
    const params = {
        has_baseline: read.answered > 0 ? 'true' : 'false',
        persona: 'parent',
        org_name: str(orgData?.name || ''),
        org_sport: sport.id,
        member_type: str(user?.memberType || 'parent'),
        age_tier: resolveAgeTier(user) || 'adult',
        safety_flag: 'false',
        pulse_due: 'false',
        voice,
        ...(journalContext ? { journal_context: journalContext } : {})
    };
    const summary = [
        `Audience voice: ${voice}. Member type: PARENT of a player in ${orgData?.name || 'this organization'}.`,
        read.answered > 0
            ? `PARENT CHECK-IN (this parent's own observations about their player, answered by the parent; NOT the player's answers, which this parent cannot see and you do not have):\n- Keep an eye on: ${fmt(read.watch) || 'nothing flagged'}\n- Steady: ${fmt(read.steady) || 'n/a'}\n- Going well: ${fmt(read.good) || 'n/a'}`
            : 'No parent check-in yet. (It is a three-minute set of questions about how their player has seemed lately, offered on the Parent Hub. Mention it only if it would help.)',
        guides.length ? `Talk guides the Parent Hub suggests for the flagged areas: ${guides.join('; ')}.` : null,
        journalContext ? `Journal (shared with consent):\n${journalContext}` : null
    ].filter(Boolean).join('\n');
    return { params, summary, next: null, pulseDue: false, hasData: read.answered > 0 };
};

// The persona the app is rendering, from the resolved surface. Sent as a claim:
// the gateway honours it only for staff accounts (super-admin preview); a
// member's persona is always resolved server-side from their own docs.
export const personaForSurface = (surface, { ageTier, voice } = {}) => {
    if (surface === 'parent') return 'parent';
    if (surface === 'family') return 'family';
    if (surface === 'coach') return 'coach';
    if (surface === 'executive') return 'staff';
    if (surface === 'kidz' || surface === 'teen' || ageTier === 'kid' || ageTier === 'teen') return 'youth';
    return voice === 'corporate' ? 'employee' : voice === 'community' ? 'community' : voice === 'responder' ? 'responder' : 'athlete';
};

export const buildCoachContext = ({ scores, user, orgData, journalContext = null, surface = null }) => {
    if (surface === 'parent') return buildParentCoachContext({ user, orgData, journalContext });
    const hasData = !!scores?.hasData;
    const pulseDue = isPulseDueFor(user, hasData);
    const completed = normalizePathProgress(user?.pathProgress);
    const nodes = hasData ? buildPathNodes({ scores, isPulseDue: pulseDue }) : [];
    const next = pickRecommendedNode(nodes, completed, { hasData, isPulseDue: pulseDue });
    const voice = voiceFor(orgData);
    const quadrantId = scores?.quadrant?.id || 'unknown';
    const low = (scores?.insights?.low || []).map(m => m.label);
    const high = (scores?.insights?.high || []).map(m => m.label);
    const labels = scores?.labels || {};

    const params = {
        has_baseline: hasData ? 'true' : 'false',
        quadrant: quadrantId,
        quadrant_read: QUADRANT_READ[quadrantId] || QUADRANT_READ.unknown,
        wellbeing_tier: str(scores?.wellbeingTier || 'Unknown'),
        performance_score: hasData && Number.isFinite(scores?.perfScore) ? String(Math.round(scores.perfScore)) : '',
        wellbeing_score: hasData && Number.isFinite(scores?.wellbeingScore) ? String(Math.round(scores.wellbeingScore)) : '',
        stress_label: str(labels.stress || 'Unknown'),
        sleep_label: str(labels.sleep || 'Unknown'),
        focus_label: str(labels.focus || 'Unknown'),
        safety_flag: scores?.safetyFlag ? 'true' : 'false',
        pulse_due: pulseDue ? 'true' : 'false',
        next_step: next ? str(next.title) : '',
        next_step_route: next ? str(next.route) : '',
        low_metrics: low.join(', '),
        high_metrics: high.join(', '),
        voice,
        member_type: str(user?.memberType || 'athlete'),
        age_tier: resolveAgeTier(user) || 'adult',
        persona: personaForSurface(surface, { ageTier: resolveAgeTier(user), voice }),
        org_name: str(orgData?.name || ''),
        org_sport: resolveSport(orgData).id,
        ...(journalContext ? { journal_context: journalContext } : {})
    };

    // The org's Help & Support Pathways, so the coach names the same people and
    // routes the app does (data/supportPathways.js; org opt-in).
    const pathways = resolveSupportPathways(orgData);
    const pathwayLines = [];
    if (pathways) {
        if (surface === 'coach' && pathways.coach) {
            const c = pathways.coach;
            pathwayLines.push(`COACH PATHWAY (this organization's own guidance for a coach when a player comes to talk; it is on the team dashboard as "${c.title}". Route the coach the same way):`);
            if (c.listen?.lines) pathwayLines.push(`- ${c.listen.title}: ${c.listen.lines.join(' / ')}`);
            if (c.avoid?.items) pathwayLines.push(`- ${c.avoid.title}: ${c.avoid.items.join(' ')}`);
            if (c.next?.note) pathwayLines.push(`- ${c.next.title}: ${c.next.ask} ${c.next.note}`);
            if (c.urgent?.items) pathwayLines.push(`- ${c.urgent.title}: ${c.urgent.items.join(' ')}`);
            if (c.escalation?.routes) pathwayLines.push(`- ${c.escalation.title}: ${c.escalation.routes.map(r => `${r.label} (${r.text})`).join('; ')}`);
        } else if (surface !== 'coach' && surface !== 'executive') {
            const sections = memberPathwaysFor(pathways, { memberType: user?.memberType, ageTier: resolveAgeTier(user) });
            const ask = sections.find(sec => Array.isArray(sec.people));
            if (ask) pathwayLines.push(`WHO THIS MEMBER CAN TALK TO (the organization's own list, shown under Talk to Someone): ${ask.people.join('; ')}.`);
            const incident = sections.find(sec => sec.id === 'incident');
            if (incident?.title) pathwayLines.push(`If something happened in ${resolveSport(orgData).sport}: the app's "${incident.title}" section tells them to tell an adult they trust; the adults route it, never the player.`);
        }
    }

    const summary = [
        `Audience voice: ${voice}. Member type: ${params.member_type}.`,
        (params.age_tier === 'kid' || params.age_tier === 'teen')
            ? 'YOUTH MEMBER (under 18). Plain language, no diagnoses or clinical terms, keep replies short, and for anything heavy point them to a trusted adult and the youth support line shown in the app (Kids Help Phone).'
            : null,
        hasData
            ? `Performance ${params.performance_score}/100, wellbeing ${params.wellbeing_score}/100 — quadrant "${scores?.quadrant?.label || quadrantId}": ${params.quadrant_read}`
            : 'No baseline assessment yet.',
        hasData ? `Wellbeing tier: ${params.wellbeing_tier}. Stress: ${params.stress_label}. Sleep: ${params.sleep_label}. Focus: ${params.focus_label}.` : null,
        hasData && low.length ? `Lower performance areas: ${low.join(', ')}. Stronger areas: ${high.join(', ') || 'n/a'}.` : null,
        scores?.safetyFlag ? 'SAFETY FLAG: recent answers indicate the member may be at risk. Respond gently, do not coach through it — encourage them to use the Talk to Someone support option shown in the app, and to contact 9-8-8 (Canada) if they are in danger.' : null,
        pulseDue ? 'Their weekly pulse check-in is due (the app is prompting them to take it).' : null,
        next ? `Their current Guided Path step: "${next.title}".` : null,
        ...pathwayLines,
        journalContext ? `Journal (shared with consent):\n${journalContext}` : null
    ].filter(Boolean).join('\n');

    return { params, summary, next, pulseDue, hasData };
};

// Conversation starters that follow the person's state, not just performance.
export const starterPrompts = ({ scores, voice = 'sport' }) => {
    const hasData = !!scores?.hasData;
    const q = scores?.quadrant?.id;
    const tier = scores?.wellbeingTier;
    const corp = voice === 'corporate';
    if (!hasData) {
        return ['What does the baseline actually measure?', 'How private are my answers?', 'What should I do first?'];
    }
    if (q === 'needs_support' || tier === 'At Risk') {
        return ['Talk me through a low week', corp ? "I'm anxious about what's coming up at work" : "I'm anxious about what's coming up", 'Who can I talk to?'];
    }
    if (q === 'running_hot' || tier === 'Elevated') {
        return ['What should I drop this week?', 'Help me slow down without losing ground', "I haven't been sleeping well"];
    }
    if (q === 'building') {
        return ['What should I work on first?', "Help me build a routine I'll keep", 'How do I stay motivated on flat days?'];
    }
    return [corp ? 'Help me prepare for a big meeting' : 'Help me prepare for a big moment', 'How do I keep this going?', 'Give me a two-minute focus drill'];
};
